import * as chrono from 'chrono-node';
import type { Kind, List, Priority } from '../db/types';
import { formatDueShort } from './dates';

export type ChipKind = 'due' | 'pri' | 'list' | 'tag';

export interface Chip {
  kind: ChipKind;
  value: string;
}

export interface ParsedQuickAdd {
  /** What is left once the date, priority, list and tags have been lifted out. */
  title: string;
  due_at: string | null;
  priority: Priority;
  list_id: string | null;
  /**
   * The `#name` as written, whether or not it matched an existing list. The
   * caller decides what an unmatched name means — creating it on commit is
   * what makes lists reachable without a management screen.
   */
  listName: string | null;
  tags: string[];
  chips: Chip[];
}

/** Character ranges consumed by a token, so the title can be rebuilt without them. */
type Span = [start: number, end: number];

const PRIORITY_RE = /(^|\s)p([1-4])\b/gi;
const LIST_RE = /(^|\s)#([\w][\w/-]*)/g;
const TAG_RE = /(^|\s)@([\w][\w/-]*)/g;

/**
 * Parses `buy milk tomorrow 5pm p1 #home @errands` into structured fields plus
 * the chips shown beneath the input.
 *
 * The raw text is never rewritten — this is a read of it. Everything the parser
 * claims is visible as a chip, so nothing is lifted out silently.
 */
export function parseQuickAdd(
  raw: string,
  lists: List[] = [],
  ref: Date = new Date(),
): ParsedQuickAdd {
  const spans: Span[] = [];
  const chips: Chip[] = [];

  // ── Priority ──────────────────────────────────────────────
  let priority: Priority = 4;
  for (const m of raw.matchAll(PRIORITY_RE)) {
    const lead = m[1]!.length;
    spans.push([m.index! + lead, m.index! + m[0].length]);
    priority = Number(m[2]) as Priority;
  }

  // ── List (#name) ──────────────────────────────────────────
  let list_id: string | null = null;
  let listLabel: string | null = null;
  for (const m of raw.matchAll(LIST_RE)) {
    const name = m[2]!;
    const match = lists.find((l) => l.name.toLowerCase() === name.toLowerCase());
    spans.push([m.index! + m[1]!.length, m.index! + m[0].length]);
    list_id = match?.id ?? null;
    // An unknown #name still shows as a chip — it just does not resolve to a
    // list yet. Silently dropping it would be worse than showing it unmatched.
    listLabel = match?.name ?? name;
  }

  // ── Tags (@path) ──────────────────────────────────────────
  const tags: string[] = [];
  for (const m of raw.matchAll(TAG_RE)) {
    spans.push([m.index! + m[1]!.length, m.index! + m[0].length]);
    if (!tags.includes(m[2]!)) tags.push(m[2]!);
  }

  // ── Date, via chrono ──────────────────────────────────────
  // Parsed on the text with the sigil tokens already blanked out, so a list
  // called #may or a tag called @march cannot be read as a date.
  let due_at: string | null = null;
  const forChrono = blank(raw, spans);
  const [dateResult] = chrono.parse(forChrono, ref, { forwardDate: true });
  if (dateResult) {
    const date = dateResult.start.date();
    // chrono implies a time when none was written; a bare day means midnight,
    // which is how the rest of the app spells "no particular time".
    if (!dateResult.start.isCertain('hour')) date.setHours(0, 0, 0, 0);
    due_at = date.toISOString();
    spans.push([dateResult.index, dateResult.index + dateResult.text.length]);
  }

  if (due_at) chips.push({ kind: 'due', value: formatDueShort(due_at) });
  if (priority !== 4) chips.push({ kind: 'pri', value: `P${priority}` });
  if (listLabel) chips.push({ kind: 'list', value: listLabel });
  for (const tag of tags) chips.push({ kind: 'tag', value: tag });

  return { title: strip(raw, spans), due_at, priority, list_id, listName: listLabel, tags, chips };
}

/** Replaces each span with spaces, preserving every other index in the string. */
function blank(raw: string, spans: Span[]): string {
  const chars = [...raw];
  for (const [start, end] of spans) {
    for (let i = start; i < end && i < chars.length; i++) chars[i] = ' ';
  }
  return chars.join('');
}

function strip(raw: string, spans: Span[]): string {
  return blank(raw, spans).replace(/\s+/g, ' ').trim();
}

/**
 * A capture whose text begins a new line, or runs past a sentence, is a note.
 * Everything else is a task. Mobile's Task/Note toggle overrides this.
 */
export function inferKind(raw: string): Kind {
  return raw.includes('\n') ? 'note' : 'todo';
}
